import React from 'react'
import { Bot } from 'lucide-react'

import { UserDisplay } from '@/components/UserDisplay/UserDisplay'
import { EntityAuditLogEntry } from '@/schema'
import { getAvatarColor } from '@/utils/avatar'
import { cn } from '@/utils/common'
import { SYSTEM_USER_ID } from '@/utils/constants'

import { getAbsoluteTime, getLogTypeIcon, getRelativeTime } from '../utils'

import { AuditCardDescription } from './AuditCardDescription'

export interface AuditCardProps {
  entry: EntityAuditLogEntry
  /** Pass isToday=true so the time column shows relative time */
  isToday: boolean
  renderDescription?: (entry: EntityAuditLogEntry) => React.ReactNode
}

export const AuditCard: React.FC<AuditCardProps> = ({
  entry,
  isToday,
  renderDescription,
}) => {
  const { user, created_at, log_type } = entry
  const displayTime = isToday
    ? getRelativeTime(created_at)
    : getAbsoluteTime(created_at)

  const LogIcon = getLogTypeIcon(log_type)
  const ringBg = user.avatar_url ? 'bg-gray-200' : getAvatarColor(user.id).bg

  return (
    <div className={cn('flex gap-3 py-3')}>
      {/* Avatar with action-type icon badge */}
      <div className="relative flex-shrink-0 w-10 h-10">
        {/* Colored ring — same hue as avatar bg, extends 6px outside, 25% opacity */}
        <div
          className={cn('absolute -inset-0.5 rounded-full opacity-55', ringBg)}
        />
        {user.id === SYSTEM_USER_ID ? (
          <div className="relative z-10 flex items-center justify-center w-10 h-10 rounded-full bg-pink-100 text-pink-500">
            <Bot size={20} />
          </div>
        ) : (
          <UserDisplay
            user={user}
            showName={false}
            avatarSize="size-10"
            className="relative z-10 border"
          />
        )}
        <span className="absolute -bottom-1 -right-1 z-20 flex items-center justify-center w-5 h-5 bg-gray-100 rounded-full border-solid border-white border-2">
          <LogIcon size={10} className="text-gray-light-900" />
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <UserDisplay
            user={user}
            showAvatar={false}
            textClassName="text-sm font-semibold text-primary-900 leading-snug"
          />
          <span className="text-xs text-secondary-500 flex-shrink-0 whitespace-nowrap">
            {displayTime}
          </span>
        </div>
        <div className="flex-1 min-w-0 flex items-start">
          {renderDescription ? (
            <p className="mt-0.5 text-sm text-secondary-700 leading-normal m-0">
              {renderDescription(entry)}
            </p>
          ) : (
            <p className="mt-0.5 text-sm text-secondary-700 leading-normal m-0">
              <AuditCardDescription entry={entry} />
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuditCard
